import {
  validateCustomizationsOutOfStock,
  validateProductsOutOfStock,
} from 'features/menu-page/domain';
import { menuPageAdapter } from './menuPage.adapter';
import { newMenuPageAdapter } from './newMenuPage.adapter';
import { menuPageRepository } from './menuPage.repository';
import { newMenuPageRepository } from './newMenuPage.repository';
import { publishEvent, subscribeEvent, unsubscribeEvent } from 'shared/libs';

import type {
  FullMenuCatalog,
  ITurnOffCustomization,
  ITurnOffCustomizationList,
  ITurnOffProduct,
  MenuOutOfStockParcial,
  TurnOnCustomization,
  TurnOnProduct,
} from 'features/menu-page/domain';
import type { BaseRouteParams } from './menuPage.request';

export function menuPageDialogOpened() {
  const EVENT_NAME = 'MENU_PAGE_DIALOG_OPENED';
  type Detail = boolean;
  type EventData = CustomEvent<Detail>;
  type ListenerFN = (e: EventData) => void;
  let theListener: ListenerFN = () => undefined;
  return {
    dispatch: (d: Detail) => publishEvent(EVENT_NAME, d),
    remove: () => unsubscribeEvent(EVENT_NAME, theListener),
    add: (lt: ListenerFN) => {
      theListener = lt;
      subscribeEvent(EVENT_NAME, theListener);
    },
  };
}

export async function setTurnOnProductByStoreId(params: TurnOnProduct) {
  const payload = menuPageAdapter.toTurnOnProductReq(params);
  const _resp = await menuPageRepository.updateTurnOnProduct({ payload });
  return _resp.status === 200;
}

export function menuPageService() {
  function getStoresByProfile() {
    return menuPageRepository.readStoresByProfile();
  }

  async function getMigrationStatus(params: BaseRouteParams) {
    const { storeId } = params;
    const _resp = await newMenuPageRepository.readMigrationStatus({ storeId });
    return _resp.status === 200;
  }

  async function getFullMenuCatalog(params: GetFullMenuCatalogParams) {
    const { storeId, isMigrated } = params;
    if (isMigrated) {
      const _fnm = await newMenuPageRepository.readFullMenuCatalog({ storeId });
      return newMenuPageAdapter.toFullMenu(_fnm.data);
    }
    const _fm = await menuPageRepository.readFullMenuCatalog({ storeId });
    return menuPageAdapter.toFullMenu(_fm.data);
  }

  async function getOutOfStockItems(params: BaseRouteParams) {
    const { storeId } = params;
    const _out = await menuPageRepository.readOutOfStock({ storeId });
    return menuPageAdapter.toOutOfStock(_out.data);
  }

  async function validateOutOfStock(params: ValidateOutOfStockParams) {
    const { storeId, isMigrated, catalog } = params;
    const _p = validateProductsOutOfStock(catalog.products);
    const _c = validateCustomizationsOutOfStock(catalog.customizationGroups);
    let oosPartial: MenuOutOfStockParcial = {
      products: [],
      customizations: [],
    };
    let result: {
      quantity: number;
      products: FullMenuCatalog['products'];
      customizations: FullMenuCatalog['customizationGroups'][0]['items'];
    };
    if (isMigrated) {
      result = {
        quantity: _p.length + _c.length,
        products: _p,
        customizations: _c,
      };
      return result;
    }
    try {
      oosPartial = await getOutOfStockItems({ storeId });
    } finally {
      result = {
        quantity: _p.length + _c.length,
        products: _p.map(product => ({
          ...product,
          suspensionInfo: {
            reason: null,
            shutdownDate:
              oosPartial.products.find(p => p.id === product.id)
                ?.shutdownDate ?? null,
            activationDate:
              oosPartial.products.find(p => p.id === product.id)
                ?.activationDate ?? null,
          },
        })),
        customizations: _c.map(custom => ({
          ...custom,
          suspensionInfo: {
            reason: null,
            shutdownDate:
              oosPartial.customizations.find(p => p.id === custom.id)
                ?.shutdownDate ?? null,
            activationDate:
              oosPartial.customizations.find(p => p.id === custom.id)
                ?.activationDate ?? null,
          },
        })),
      };
    }
    return result;
  }

  async function turnOffProduct(params: ITurnOffProduct) {
    const payload = menuPageAdapter.toTurnOffProductReq(params);
    const _resp = await menuPageRepository.updateTurnOffProduct({ payload });
    return _resp.status === 200;
  }

  async function turnOnCustomization(params: TurnOnCustomization) {
    const payload = menuPageAdapter.toTurnOnCustomizationReq(params);
    const _resp = await menuPageRepository.updateTurnOnCustomization({
      payload,
    });
    return _resp.status === 200;
  }

  async function turnOffCustomization(params: ITurnOffCustomization) {
    const payload = menuPageAdapter.toTurnOffCustomizationReq(params);
    const _resp = await menuPageRepository.updateTurnOffCustomization({
      payload,
    });
    return _resp.status === 200;
  }

  async function turnOffCustomizationList(params: ITurnOffCustomizationList) {
    const payload = menuPageAdapter.toTurnOffCustomizationListReq(params);
    const _resp = await menuPageRepository.updateTurnOffCustomizationList({
      payload,
    });
    return _resp.status === 200;
  }

  return {
    getStoresByProfile,
    getMigrationStatus,
    getFullMenuCatalog,
    validateOutOfStock,
    turnOnProduct: setTurnOnProductByStoreId,
    turnOffProduct,
    turnOnCustomization,
    turnOffCustomization,
    turnOffCustomizationList,
    someDialogOpened: menuPageDialogOpened,
  };
}

interface GetFullMenuCatalogParams extends BaseRouteParams {
  isMigrated: boolean;
}

interface ValidateOutOfStockParams extends BaseRouteParams {
  isMigrated: boolean;
  catalog: FullMenuCatalog;
}
